// =============================================================================
// src/types/common.types.ts
// =============================================================================

import type { buttonVariants } from "@ui/button-variants";
import type { VariantProps } from "class-variance-authority";
import type { LucideIcon } from "lucide-react";
import * as React from "react";
import type { RegisterOptions } from "react-hook-form";
export type UUID = string;
export type ISODateString = string;

export type Theme = "light" | "dark";

// 1. La "Fuente de Verdad" (Data + Tipos literales)
export const APP_LANGUAGES = [
  { code: "en", label: "english" },
  { code: "es", label: "spanish" },
  { code: "pt", label: "portuguese" },
  { code: "fr", label: "french" },
  { code: "it", label: "italian" },
] as const;

// 2. Derivamos el tipo 'Language' del array anterior
// Esto genera exactamente: 'es' | 'en' | 'pt' | 'fr' | 'it'
export type Language = (typeof APP_LANGUAGES)[number]["code"];

// 3. Tipo para el objeto completo (útil para props de componentes)
export type LevelType = "standard" | "basement" | "exterior" | "rooftop";
export type IconType =
  | LucideIcon
  | React.ComponentType<{
      size?: number;
      className?: string;
      strokeWidth?: number;
    }>;

export type ReorderItem = {
  id: UUID;
  sort_order: number;
};

export type CameraAngles = { azimuth: number; polar: number };
export type CameraAnglesRef = React.RefObject<CameraAngles>;

export interface AppContextType {
  theme: Theme;
  toggleTheme: () => void;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  to?: string;
  external?: boolean;
  label?: string;
  loading?: boolean;
  icon?: IconType;
  iconPosition?: "left" | "right";
  state?: string;
  isDraggable?: boolean;
  isActive?: boolean;
  isFocused?: boolean;
  floatingLabel?: string;
  floatingPosition?: "top" | "right" | "bottom" | "left";
  /**
   * Collapses the button to an icon-only square below a breakpoint (label
   * hidden, padding dropped, width = height); from that breakpoint up the
   * label shows. `true` = `sm` (default); pass a breakpoint to raise the
   * collapse point (e.g. `"xl"` for crowded nav bars).
   * Requires `icon` + `label`. Keeps an accessible name via `aria-label`.
   */
  responsiveLabel?: boolean | "sm" | "md" | "lg" | "xl" | "2xl";
}

export interface FormField {
  key: string;
  label: string;
  type:
    | "audio"
    | "video"
    | "video-source-tabs"
    | "audio-source-tabs"
    | "image-source-tabs"
    | "text"
    | "number"
    | "password"
    | "file"
    | "image"
    | "media"
    | "textarea"
    | "select"
    | "boolean"
    | "readonly"
    | "email"
    | "translations_name"
    | "translations_content"
    | "image-list";
  rules?: RegisterOptions;
  defaultValue?: string | number | boolean | null;
  placeholder?: string;
  rows?: number;
  icon?: LucideIcon;
  options?: { label: string; value: string }[];
  currentUrl?: string | null;
  urlKey?: string;
  defaultTab?: "file" | "url";
  baseLang?: string;
  languages?: string[];
  /** Display names for non-app language codes (custom languages), by code. */
  languageNames?: Record<string, string>;
  /** For translations_name — seed values `{ lang: { name } }`. */
  existingTranslations?: Record<string, { name?: string }>;
  /** For translations_content — seed values `{ lang: { title, description } }`. */
  contentTranslations?: Record<
    string,
    { title?: string; description?: string }
  >;
  /** For translations_content — hide the description input (title-only i18n). */
  titleOnly?: boolean;
  /** For image-list — the hotspot's saved ADDITIONAL images (seeds the draft). */
  currentImages?: { media_id: string; url: string; name?: string }[];
}
